import { useCallback } from 'react';
import { useFormikContext } from 'formik';

import Grid from '@mui/material/Grid';

import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useFindAllDocumentTemplatesQuery } from '../../../pages/DocumentAndSectionTemplates/hooks/useFindAllDocumentTemplatesQuery.hook';
import { Loading } from '../../../components/Loading';
import {
  IdAutocomplete,
  IdMultiAutocomplete,
} from '../../../components/IdAutocomplete';
import type { IdOption } from '../../../components/IdAutocomplete';

import type { ResolvableMissionTypeForm_ResolvableMissionType } from '../fragments/resolvable-mission-type.fragment';

interface Props {
  readonly id: string;
}

export const ResolvableMissionTypeTemplatesFormStep = ({}: Readonly<Props>) => {
  const {
    errors,
    initialValues,
    setFieldTouched,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<ResolvableMissionTypeForm_ResolvableMissionType>();

  const { documentTypesCodeMap, genericStatusesCodeMap } =
    useReferencesContext();

  const {
    data: allDocumentTemplateQuery,
    isSuccess: allDocumentTemplateQueryIsSuccess,
  } = useFindAllDocumentTemplatesQuery();

  const allReportTemplates = allDocumentTemplateQueryIsSuccess
    ? allDocumentTemplateQuery.findAllDocumentTemplates.filter(
        (template) =>
          (template.document.typeId === documentTypesCodeMap.Report._id &&
            template.statusId === genericStatusesCodeMap.Enabled._id) ||
          initialValues.reportTemplateIds?.includes(template._id),
      )
    : EMPTY_ARRAY;

  const allQuotationTemplates = allDocumentTemplateQueryIsSuccess
    ? allDocumentTemplateQuery.findAllDocumentTemplates.filter(
        (template) =>
          (template.document.typeId === documentTypesCodeMap.Quotation._id &&
            template.statusId === genericStatusesCodeMap.Enabled._id) ||
          template._id === initialValues.quotationTemplateId,
      )
    : EMPTY_ARRAY;

  const handleReportTemplateIdsChange = useCallback(
    (ids: readonly Id[]) => {
      void setFieldValue('reportTemplateIds', ids);
    },
    [setFieldValue],
  );

  const handleQuotationTemplateIdChange = useCallback(
    (id: Id | typeof EMPTY_STRING) => {
      void setFieldValue('quotationTemplateId', id || EMPTY_STRING);
    },
    [setFieldValue],
  );

  const handleQuotationTemplateIdBlur = useCallback(() => {
    void setFieldTouched('quotationTemplateId');
  }, [setFieldTouched]);

  return (
    <>
      {!allDocumentTemplateQueryIsSuccess && <Loading />}
      <Grid container spacing={2}>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <IdMultiAutocomplete<Id, IdOption<Id>>
            label="Modèles de Rapport"
            loading={!allDocumentTemplateQueryIsSuccess}
            onChange={handleReportTemplateIdsChange}
            options={allReportTemplates.map((template) => ({
              id: template._id,
              label: template.label,
            }))}
            value={values.reportTemplateIds ?? EMPTY_ARRAY}
          />
        </Grid>
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <IdAutocomplete<Id, IdOption<Id>>
            error={
              !!(errors.quotationTemplateId && touched.quotationTemplateId)
            }
            helperText={
              errors.quotationTemplateId && touched.quotationTemplateId
                ? errors.quotationTemplateId
                : undefined
            }
            label="Modèle de Devis"
            loading={!allDocumentTemplateQueryIsSuccess}
            name="quotationTemplateId"
            onBlur={handleQuotationTemplateIdBlur}
            onChange={handleQuotationTemplateIdChange}
            options={allQuotationTemplates.map((template) => ({
              id: template._id,
              label: template.label,
            }))}
            value={values.quotationTemplateId ?? EMPTY_STRING}
          />
        </Grid>
      </Grid>
    </>
  );
};
